// Phase B — Customer Portal · New Request form
//
// Customer-safe subset of the freight-deal create form. No internal pricing
// inputs (margin / cost). The customer can suggest a `target_price` which
// the server maps to `celar` (the forwarder's celar / target).
// On submit, the request lands in the linked forwarder's Freight Deals at
// stage='registered', tagged created_via_customer_portal=true, with an
// in-app notification to the forwarder.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilePlus, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useCreateLogistics } from "../../api/logistics";
import { useT } from "../../lib/i18n";

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const inputStyle = {
  background: "var(--bg, #0a0c14)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "var(--text, #f0f2f8)",
  fontFamily: "'DM Mono', monospace",
  fontSize: 12,
  padding: "9px 10px",
  borderRadius: 6,
  width: "100%",
};

const Field = ({ label, required, children, testId }) => (
  <label className="block" data-testid={testId}>
    <div style={{ ...mono(9, "var(--text-dim, #9aa1b3)"), letterSpacing: "1px", textTransform: "uppercase", marginBottom: 4 }}>
      {label}{required && <span style={{ color: "#ff8a8a" }}> *</span>}
    </div>
    {children}
  </label>
);

const Stop = ({ prefix, value, onChange, testIdPrefix, t }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={testIdPrefix}>
    <Field label={`${prefix} ${t("portal.form.city")}`} required testId={`${testIdPrefix}-city`}>
      <input value={value.city || ""} onChange={(e) => onChange({ ...value, city: e.target.value })} style={inputStyle} />
    </Field>
    <Field label={`${prefix} ${t("portal.form.country")}`} testId={`${testIdPrefix}-country`}>
      <input maxLength={2} value={value.country || ""} onChange={(e) => onChange({ ...value, country: e.target.value.toUpperCase() })} style={inputStyle} placeholder="DE" />
    </Field>
    <Field label={`${prefix} ${t("portal.form.postal")}`} testId={`${testIdPrefix}-postal`}>
      <input value={value.postal_code || ""} onChange={(e) => onChange({ ...value, postal_code: e.target.value })} style={inputStyle} />
    </Field>
    <Field label={`${prefix} ${t("portal.form.street")}`} testId={`${testIdPrefix}-street`}>
      <input value={value.street || ""} onChange={(e) => onChange({ ...value, street: e.target.value })} style={inputStyle} />
    </Field>
    <Field label={`${prefix} ${t("portal.form.window_from")}`} testId={`${testIdPrefix}-from`}>
      <input type="datetime-local" value={value.window_start_at || ""} onChange={(e) => onChange({ ...value, window_start_at: e.target.value })} style={inputStyle} />
    </Field>
    <Field label={`${prefix} ${t("portal.form.window_to")}`} testId={`${testIdPrefix}-to`}>
      <input type="datetime-local" value={value.window_end_at || ""} onChange={(e) => onChange({ ...value, window_end_at: e.target.value })} style={inputStyle} />
    </Field>
  </div>
);

export default function PortalNewRequest() {
  const navigate = useNavigate();
  const { t } = useT();
  const create = useCreateLogistics();
  const [form, setForm] = useState({
    deal_name: "",
    aru_megnevezese: "",
    autotipus: "",
    suly_kg: "",
    rakmeter: "",
    kobmeter: "",
    target_price: "",
    pickup: {},
    dropoff: {},
    note: "",
  });
  const [error, setError] = useState(null);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!form.deal_name || form.deal_name.length < 2) {
      setError(t("portal.form.err_summary"));
      return;
    }
    if (!form.pickup.city && !form.dropoff.city) {
      setError(t("portal.form.err_location"));
      return;
    }
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const stop = (s) => {
      if (!s || !s.city) return null;
      return {
        country: (s.country || "").toUpperCase().slice(0, 2) || null,
        city: s.city,
        postal_code: s.postal_code || "",
        street: s.street || "",
        house_number: "",
        window_start_at: s.window_start_at || null,
        window_end_at: s.window_end_at || null,
      };
    };
    const body = {
      deal_name: form.deal_name,
      request_type: "spot",
      aru_megnevezese: form.aru_megnevezese || null,
      autotipus: form.autotipus || null,
      suly_kg: num(form.suly_kg),
      rakmeter: num(form.rakmeter),
      kobmeter: num(form.kobmeter),
      target_price: num(form.target_price),
      pickups: [stop(form.pickup)].filter(Boolean),
      dropoffs: [stop(form.dropoff)].filter(Boolean),
      megjegyzes: form.note || null,
    };
    Object.keys(body).forEach((k) => { if (body[k] == null || body[k] === "") delete body[k]; });
    try {
      const created = await create.mutateAsync(body);
      toast.success(t("portal.form.toast_submitted", { id: created.id }));
      navigate("/portal/my-requests");
    } catch (e2) {
      const det = e2?.response?.data?.detail;
      const msg = typeof det === "string" ? det : (det?.message || e2?.message || "Failed to submit");
      setError(msg);
      if (det?.code === "no_linked_forwarder") {
        setError(t("portal.form.err_no_forwarder"));
      }
    }
  };

  return (
    <div data-testid="portal-new-request-page">
      <header className="mb-5">
        <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 28px)", color: "var(--text, #f0f2f8)" }}>
          <FilePlus size={22} style={{ display: "inline", marginRight: 8, marginBottom: 4 }} />
          {t("portal.form.title")}
        </h1>
        <div className="mt-1" style={mono(11, "var(--text-dim, #c5cad8)")}>
          {t("portal.form.subtitle")}
        </div>
      </header>

      <form onSubmit={submit} className="space-y-5">
        <section
          className="rounded p-4 space-y-4"
          style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
        >
          <div style={{ ...mono(10, "var(--text-dim, #c5cad8)", 700, "1.2px"), textTransform: "uppercase" }}>
            {t("portal.form.section_summary")}
          </div>
          <Field label={t("portal.form.deal_name")} required testId="portal-form-deal-name">
            <input
              value={form.deal_name}
              onChange={(e) => set("deal_name", e.target.value)}
              style={inputStyle}
              placeholder={t("portal.form.deal_name_placeholder")}
              data-testid="portal-form-deal-name-input"
            />
          </Field>
        </section>

        <section
          className="rounded p-4 space-y-4"
          style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
        >
          <div style={{ ...mono(10, "var(--text-dim, #c5cad8)", 700, "1.2px"), textTransform: "uppercase" }}>
            {t("portal.form.section_pickup")}
          </div>
          <Stop prefix={t("portal.form.section_pickup")} value={form.pickup} onChange={(v) => set("pickup", v)} testIdPrefix="portal-form-pickup" t={t} />
        </section>

        <section
          className="rounded p-4 space-y-4"
          style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
        >
          <div style={{ ...mono(10, "var(--text-dim, #c5cad8)", 700, "1.2px"), textTransform: "uppercase" }}>
            {t("portal.form.section_dropoff")}
          </div>
          <Stop prefix={t("portal.form.section_dropoff")} value={form.dropoff} onChange={(v) => set("dropoff", v)} testIdPrefix="portal-form-dropoff" t={t} />
        </section>

        <section
          className="rounded p-4 space-y-4"
          style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
        >
          <div style={{ ...mono(10, "var(--text-dim, #c5cad8)", 700, "1.2px"), textTransform: "uppercase" }}>
            {t("portal.form.section_cargo")}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("portal.form.goods")} testId="portal-form-aru">
              <input value={form.aru_megnevezese} onChange={(e) => set("aru_megnevezese", e.target.value)} style={inputStyle} placeholder="auto parts" />
            </Field>
            <Field label={t("portal.form.vehicle_type")} testId="portal-form-autotipus">
              <input value={form.autotipus} onChange={(e) => set("autotipus", e.target.value)} style={inputStyle} placeholder="ponyvás / plandeka / tarpaulin" />
            </Field>
            <Field label={t("portal.form.weight")} testId="portal-form-suly">
              <input type="number" value={form.suly_kg} onChange={(e) => set("suly_kg", e.target.value)} style={inputStyle} />
            </Field>
            <Field label={t("portal.form.ldm")} testId="portal-form-rakmeter">
              <input type="number" step="0.1" value={form.rakmeter} onChange={(e) => set("rakmeter", e.target.value)} style={inputStyle} />
            </Field>
            <Field label={t("portal.form.volume")} testId="portal-form-kobmeter">
              <input type="number" step="0.1" value={form.kobmeter} onChange={(e) => set("kobmeter", e.target.value)} style={inputStyle} />
            </Field>
          </div>
        </section>

        <section
          className="rounded p-4 space-y-4"
          style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(155,89,182,0.30)" }}
        >
          <div style={{ ...mono(10, "#c89adb", 700, "1.2px"), textTransform: "uppercase" }}>
            {t("portal.form.section_target_price")}
          </div>
          <Field label={t("portal.form.target_price_label")} testId="portal-form-target-price">
            <input
              type="number"
              step="0.01"
              value={form.target_price}
              onChange={(e) => set("target_price", e.target.value)}
              style={inputStyle}
              placeholder="e.g. 1200"
              data-testid="portal-form-target-price-input"
            />
            <div className="mt-1" style={mono(10, "var(--text-dim, #9aa1b3)")}>
              {t("portal.form.target_price_help")}
            </div>
          </Field>
          <Field label={t("portal.form.note")} testId="portal-form-note">
            <textarea
              rows={3}
              value={form.note}
              onChange={(e) => set("note", e.target.value)}
              style={{ ...inputStyle, fontFamily: "'DM Mono', monospace" }}
              placeholder={t("portal.form.note_placeholder")}
            />
          </Field>
        </section>

        {error && (
          <div
            className="rounded px-3 py-2 inline-flex items-start gap-2"
            style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.40)", color: "#ff8a8a", ...mono(11) }}
            data-testid="portal-form-error"
          >
            <AlertCircle size={13} className="mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={create.isPending}
            className="px-4 py-2 rounded inline-flex items-center gap-2"
            style={{
              background: "#7e3aa8",
              color: "#fff",
              ...mono(11, "#fff", 700, "0.8px"),
              textTransform: "uppercase",
              opacity: create.isPending ? 0.6 : 1,
              cursor: create.isPending ? "not-allowed" : "pointer",
              border: "none",
            }}
            data-testid="portal-form-submit"
          >
            {create.isPending ? <Loader2 size={13} className="animate-spin" /> : <FilePlus size={13} />}
            {create.isPending ? t("portal.form.submitting") : t("portal.form.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
